import { useContext, useMemo, useState } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { DisplayHeading, PillSearch, UnderlineTabs } from "components/ui";
import { ff } from "theme";
import { ConversationsContext } from "contexts/ConversationsDataContext";
import { getUnreadCount } from "./utils";
import { CollabDataContext } from "contexts/collabDataContext";
import { InfluencerCampaignDataContext } from "contexts/InfluencerCampaignDataContext";
import { useMarketplaceOrders } from "contexts/MarketplaceOrdersContext";
import ConversationsList, { InboxTab } from "./components/conversationsList";
import { useNavigate, Outlet } from "react-router-dom";
import { Client } from "@foodfluence/shared";

export default function Inbox() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const collabsData = useContext(CollabDataContext);
  const influencerCampaignData = useContext(InfluencerCampaignDataContext);
  const { orders: marketplaceOrders } = useMarketplaceOrders();
  const conversations = useContext(ConversationsContext);
  const [tab, setTab] = useState<InboxTab>("all");

  // Unread totals over the conversations the inbox can show (collabs + marketplace orders).
  const { unreadTotal, unreadThreads } = useMemo(() => {
    const ids = new Set([...collabsData.map((c) => c.id), ...marketplaceOrders.map((o) => o.id)]);
    let total = 0;
    let threads = 0;
    ids.forEach((id) => {
      const n = getUnreadCount(conversations[id]?.messages_log);
      total += n;
      if (n > 0) threads += 1;
    });
    return { unreadTotal: total, unreadThreads: threads };
  }, [conversations, collabsData, marketplaceOrders]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleConversationClick = (conversationId: string, client?: Client) => {
    const collab = collabsData.find((c) => c.id === conversationId);
    const marketplaceOrder = marketplaceOrders.find((o) => o.id === conversationId);
    const campaign = collab ? influencerCampaignData[collab.id] : undefined;
    navigate(`/inbox/messages/${conversationId}`, {
      state: {
        conversationId,
        campaign,
        collab,
        client,
        marketplaceOrderId: marketplaceOrder?.id,
      },
    });
  };

  return (
    <Box position="relative" fontFamily="display" maxWidth="100vw" bg="white" minH="100vh" color={ff.ink}>
      <Flex direction="column" gap="18px" px="20px" pt="24px">
        <Flex align="flex-end" justify="space-between">
          <DisplayHeading as="h1">Inbox</DisplayHeading>
          {unreadTotal > 0 && (
            <Flex
              as="span"
              h="28px"
              px="10px"
              borderRadius="full"
              bg={ff.ink}
              color={ff.lime}
              align="center"
              fontSize="13px"
              fontWeight={600}
              aria-label={`${unreadTotal} unread messages`}
            >
              {unreadTotal} new
            </Flex>
          )}
        </Flex>

        <PillSearch
          id="search-input"
          name="search"
          placeholder="Search messages"
          aria-label="Search messages"
          value={searchTerm}
          onChange={handleSearchChange}
        />

        <UnderlineTabs
          tabs={[
            { key: "all", label: "All" },
            { key: "unread", label: `Unread (${unreadThreads})` },
            { key: "submitted", label: "Submitted" },
          ]}
          active={tab}
          onChange={(k) => setTab(k as InboxTab)}
        />
      </Flex>

      <ConversationsList onConversationClick={handleConversationClick} searchTerm={searchTerm} tab={tab} />
      <Outlet />
    </Box>
  );
}
